import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { TeamMemberWithDetails } from "../../../types/team_members.types";
import { useToast } from "../../../components/toast";
import { getObjectDiff } from "../../../utils/object-diff.utils";
import { TeamMembersAPI } from "../../../APIs/team_members.api";
import { PersonalInfoApiError } from "../../../types/personal_info.types";
import { TeamMemberForm } from "../../my-team/team-members-information/team-member-form";
import { Loading } from "../../../components/loading";

export function TeamMemberEditPage() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const teamId = searchParams.get("teamId");

  const [modifiedFormData, setModifiedFormData] =
    useState<TeamMemberWithDetails | null>(null);
  const [originalFormData, setOriginalFormData] =
    useState<TeamMemberWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [apiErrors, setApiErrors] = useState<PersonalInfoApiError>({
    dni: null,
    email: null,
    licence_number: null,
  });
  const handleCancel = useCallback(() => {
    if (teamId) {
      navigate(`/dashboard/teams/view/${teamId}`);
    } else {
      navigate(-1);
    }
  }, [teamId, navigate]);

  useEffect(() => {
    const fetchTeamMember = async () => {
      if (!userId) return;

      try {
        const result = await TeamMembersAPI.getOneTeamMember(parseInt(userId));
        if (result.statusCode === 200) {
          const memberWithAddresses: TeamMemberWithDetails = {
            ...result.data,
            addresses: result.data.addresses || [
              {
                id: 0,
                owner_id: result.data.user_id,
                owner_type: 1,
                address_line: "",
                city: "",
                state: "",
                country: "",
                is_active: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            ],
          };
          setModifiedFormData(memberWithAddresses);
          setOriginalFormData(memberWithAddresses);
        } else {
          addToast({
            message: "No se encontró el miembro",
            type: "error",
            location: "bottom-center",
            duration: 10000,
          });
          handleCancel();
        }
      } catch (error) {
        addToast({
          message: "Error al cargar los datos del miembro",
          type: "error",
          location: "bottom-center",
        });
        handleCancel();
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamMember();
  }, [userId, addToast, handleCancel]);

  const handleChange = (e: React.ChangeEvent<HTMLElement>, index?: number) => {
    setApiErrors({
      dni: null,
      email: null,
      licence_number: null,
    });
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value, type } = target;

    setModifiedFormData((prev) => {
      if (!prev) return prev;

      if (type === "checkbox") {
        return {
          ...prev,
          [name]: (target as HTMLInputElement).checked,
        };
      }

      if (index !== undefined && prev.addresses) {
        return {
          ...prev,
          addresses: prev.addresses.map((address, i) =>
            i === index ? { ...address, [name]: value } : address
          ),
        };
      }

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        const parentKey = parent as keyof TeamMemberWithDetails;
        const parentObject = prev[parentKey];

        if (parentObject && typeof parentObject === "object") {
          return {
            ...prev,
            [parent]: {
              ...parentObject,
              [child]: value,
            },
          };
        }

        return {
          ...prev,
          [parent]: {
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const changes = getObjectDiff(originalFormData, modifiedFormData);

      const { id, user_id, personal_info, ...changesWithoutIds } = changes;

      if (personal_info && "user_id" in personal_info) {
        delete personal_info.user_id;
      }

      if (personal_info) {
        changesWithoutIds.personal_info = personal_info;
      }

      const response = await TeamMembersAPI.updateTeamMember(
        user_id,
        changesWithoutIds
      );
      if (response.statusCode === 200) {
        addToast({
          message: "¡Miembro actualizado!",
          type: "success",
          location: "bottom-center",
        });
        handleCancel();
      } else {
        const errorCode = response.additionalData?.errorCode;
        if (errorCode === "DNI_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            dni: "Este DNI ya está en uso.",
          }));
        } else if (errorCode === "EMAIL_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            email: "Este correo electrónico ya está en uso.",
          }));
        } else if (errorCode === "LICENSE_NUMBER_IN_USE") {
          setApiErrors((prev) => ({
            ...prev,
            licence_number: "Este número de licencia ya está en uso.",
          }));
        }
        addToast({
          message: response.message,
          type: "error",
          location: "bottom-center",
          duration: 10000,
        });
      }
    } catch (error) {
      console.error(error);
      addToast({
        message: "Error al actualizar el miembro, intente nuevamente",
        type: "error",
        location: "bottom-center",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-screen mt-10">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
          <div className="text-center text-red-600">
            Error: ID de usuario no encontrado
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen mt-10">
        <Loading />
      </div>
    );
  }

  if (!modifiedFormData) {
    return (
      <div className="min-h-screen mt-10">
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
          <div className="text-center text-red-600">
            Error: No se pudieron cargar los datos del miembro
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-10">
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex space-x-2">
          <h2 className="text-2xl font-bold text-center text-gray-600">
            Editar Miembro del Equipo
          </h2>
        </div>

        <div className="mt-6">
          <TeamMemberForm
            buttonText="Guardar"
            teamMember={modifiedFormData}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            handleChange={handleChange}
            isSubmitting={isSubmitting}
            personalInfoApiError={apiErrors}
          />
        </div>
      </div>
    </div>
  );
}
